<template>
  <div class="max-w-3xl mx-auto">
    <div class="flex justify-between items-center mb-6 pb-4" style="border-bottom: 1px solid var(--border-subtle);">
      <h2 class="" style="font-size: var(--text-2xl); color: var(--text-primary);">{{ t('profile.title') }}</h2>
      <AppButton icon-only :aria-label="t('common.close')" class="text-xl" @click="$emit('close')">×</AppButton>
    </div>

    <div class="flex flex-col gap-6">
      <!-- Profile card. Order is deliberate: photo, then who you are, then the
           one real action. The photo's own controls sit directly under it as a
           caption (links, not buttons) so they read as belonging to the photo
           rather than competing with "Edit profile". -->
      <div class="p-6 text-center" style="background: var(--bg-secondary); border: 1px solid var(--border-subtle); border-radius: 6px;">
        <div class="flex flex-col items-center gap-3">
          <AppAvatar
            size="xl"
            :image-id="avatarImageId"
            :src="user?.photoURL"
            :name="user?.displayName || user?.email || 'U'"
            :alt="user?.displayName || t('app.user_fallback')"
            style="border: 2px solid var(--border-default);"
          />
          <!-- Uploading a photo is the one thing a guest can do here that
               survives nothing — anonymous sessions get the same account
               restrictions as everywhere else.
               hide-preview: the AppAvatar above is already this photo, and it
               has the fallback chain (upload → sign-in photo → initials) that
               the picker's own empty state doesn't. -->
          <ImagePicker
            v-if="!isAnonymous"
            v-model="avatarImageId"
            purpose="avatar"
            round
            hide-preview
            links
            :alt="t('profile.photo_label')"
            empty-icon="👤"
            @update:model-value="saveAvatar"
          />
        </div>

        <div class="mt-4">
          <h3 class="mb-1" style="font-size: var(--text-2xl); color: var(--text-primary);">{{ user?.displayName || t('app.user_fallback') }}</h3>
          <p style="font-size: var(--text-sm); color: var(--text-secondary); margin-bottom: 12px;">{{ user?.email || t('profile.no_email') }}</p>
          <div class="flex gap-2 justify-center">
            <AppBadge v-if="isAnonymous" variant="warning">{{ t('profile.guest_account') }}</AppBadge>
            <AppBadge v-else-if="user?.emailVerified" variant="success">{{ t('profile.verified') }}</AppBadge>
            <AppBadge v-else variant="info">{{ t('profile.not_verified') }}</AppBadge>
          </div>
        </div>

        <AppButton v-if="!isAnonymous" variant="outline" size="sm" class="mt-5" @click="showEditProfile = true">
          {{ t('profile.edit_profile') }}
        </AppButton>
      </div>

      <!-- Account Upgrade Banner for Anonymous Users -->
      <AppAlert v-if="isAnonymous" variant="warning" :title="t('profile.upgrade_title')">
        <p class="m-0 mb-3" style="color: var(--text-secondary); font-size: var(--text-sm);">{{ t('profile.upgrade_description') }}</p>
        <AppButton class="w-full md:w-auto" style="background: var(--amber-600);" @click="handleUpgradeAccount">
          {{ t('profile.upgrade_now') }}
        </AppButton>
      </AppAlert>

      <!-- Account Details -->
      <div class="p-5" style="background: var(--surface); border: 1px solid var(--border-subtle); border-radius: 6px;">
        <p class="label-caps mb-4">{{ t('profile.account_details') }}</p>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="flex flex-col gap-1">
            <label style="font-size: var(--text-xs); color: var(--text-tertiary); font-weight: 500;">{{ t('profile.account_type') }}</label>
            <span style="font-size: var(--text-sm); color: var(--text-primary);">{{ accountType }}</span>
          </div>

          <div class="flex flex-col gap-1">
            <label style="font-size: var(--text-xs); color: var(--text-tertiary); font-weight: 500;">{{ t('profile.member_since') }}</label>
            <span style="font-size: var(--text-sm); color: var(--text-primary);">{{ formatDate(user?.metadata.creationTime) }}</span>
          </div>

          <div class="flex flex-col gap-1">
            <label style="font-size: var(--text-xs); color: var(--text-tertiary); font-weight: 500;">{{ t('profile.last_sign_in') }}</label>
            <span style="font-size: var(--text-sm); color: var(--text-primary);">{{ formatDate(user?.metadata.lastSignInTime) }}</span>
          </div>

          <div class="flex flex-col gap-1">
            <label style="font-size: var(--text-xs); color: var(--text-tertiary); font-weight: 500;">{{ t('profile.user_id') }}</label>
            <span class="font-mono break-all" style="font-size: var(--text-xs); color: var(--text-secondary);">{{ user?.uid }}</span>
          </div>
        </div>
      </div>

      <!-- Household Memberships (only for non-anonymous users) -->
      <div v-if="!isAnonymous && households.length > 0" class="p-5" style="background: var(--surface); border: 1px solid var(--border-subtle); border-radius: 6px;">
        <p class="label-caps mb-4">{{ t('profile.household_memberships') }}</p>
        <div class="flex flex-col gap-3">
          <div v-for="household in households" :key="household.id" class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 p-4" style="background: var(--bg-secondary); border-radius: 4px;">
            <div>
              <h5 class="m-0 mb-1" style="font-weight: 600; color: var(--text-primary); font-size: var(--text-sm);">{{ household.name }}</h5>
              <p class="m-0" style="font-size: var(--text-xs); color: var(--text-tertiary);">{{ getUserRole(household) }} · {{ household.members.length }} {{ t('household.members_word') }}</p>
            </div>
            <AppButton variant="outline" size="sm" class="w-full md:w-auto" @click="openHouseholdManager">
              {{ t('profile.manage') }}
            </AppButton>
          </div>
        </div>
      </div>

      <!-- Pending Invitations -->
      <div v-if="!isAnonymous && pendingInvitations.length > 0" class="p-5" style="background: var(--surface); border: 1px solid var(--border-subtle); border-radius: 6px;">
        <p class="label-caps mb-4">{{ t('household.pending_invitations', { count: pendingInvitations.length }) }}</p>
        <div class="flex flex-col gap-3">
          <div v-for="invitation in pendingInvitations" :key="invitation.id" class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 p-4" style="background: var(--bg-secondary); border: 2px solid var(--accent); border-radius: 4px;">
            <div>
              <strong class="block mb-1 text-primary">{{ invitation.householdName }}</strong>
              <p class="m-0" style="font-size: var(--text-sm); color: var(--text-tertiary);">{{ t('profile.invited_by', { name: invitation.invitedByName }) }}</p>
            </div>
            <AppButton size="sm" class="w-full md:w-auto" @click="openHouseholdManager">
              {{ t('profile.view') }}
            </AppButton>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="p-5" style="background: var(--surface); border: 1px solid var(--border-subtle); border-radius: 6px;">
        <p class="label-caps mb-4">{{ t('profile.account_actions') }}</p>
        <div class="flex flex-col gap-3">
          <ActionRow
            v-if="!isAnonymous"
            :title="t('profile.reset_password')"
            :description="t('profile.reset_password_description', { email: user?.email })"
            @click="handleResetPassword"
          />

          <ActionRow
            tone="danger"
            :title="t('profile.sign_out')"
            :description="t('profile.sign_out_description')"
            @click="handleSignOut"
          />

          <!-- Deletion must be reachable from the account surface itself, not
               only buried in Settings — app stores expect an obvious path and
               guests need one too (their data is real, just unlinked). -->
          <ActionRow
            tone="danger"
            :title="t('settings.delete_account_title')"
            :description="t('settings.delete_account_description')"
            :disabled="isDeletingAccount"
            @click="showDeleteAccountConfirm = true"
          />
        </div>
      </div>
    </div>

    <!-- Edit Profile Modal — AppModal teleports to <body>, so it stacks above
         the outer profile panel regardless of where this component is hosted. -->
    <AppModal
      v-model="showEditProfile"
      :title="t('profile.edit_profile')"
      :mobile-sheet="false"
      initial-focus="first-input"
      @close="closeEditProfile"
    >
      <form @submit.prevent="handleSaveProfile">
        <div class="mb-4">
          <AppInput
            v-model="editForm.displayName"
            type="text"
            :label="t('profile.display_name')"
            :placeholder="t('profile.display_name_placeholder')"
            :disabled="isSaving"
          />
        </div>

        <div class="mb-4">
          <AppInput
            v-model="editForm.photoURL"
            type="url"
            :label="t('profile.photo_url')"
            placeholder="https://example.com/photo.jpg"
            :hint="t('profile.photo_url_hint')"
            :disabled="isSaving"
          />
        </div>

        <div v-if="editForm.photoURL" class="mb-4">
          <label class="label-caps block mb-2">{{ t('profile.preview') }}</label>
          <AppAvatar :src="editForm.photoURL" :name="editForm.displayName || user?.email || 'U'" :alt="t('profile.preview')" size="xl" />
        </div>

        <div v-if="profileError" class="mb-4 p-2" style="background: var(--ruby-100); color: var(--ruby-700); border: 1px solid var(--ruby-200); border-radius: 3px; font-size: var(--text-xs);">{{ profileError }}</div>
        <div v-if="profileSuccess" class="mb-4 p-2" style="background: var(--forest-100); color: var(--forest-700); border: 1px solid var(--forest-200); border-radius: 3px; font-size: var(--text-xs);">{{ profileSuccess }}</div>

        <div class="flex gap-3 pt-4">
          <AppButton variant="ghost" class="flex-1" :disabled="isSaving" @click="closeEditProfile">
            {{ t('common.cancel') }}
          </AppButton>
          <AppButton type="submit" class="flex-1" :disabled="isSaving || !editForm.displayName.trim()">
            {{ isSaving ? t('common.saving') : t('profile.save_changes') }}
          </AppButton>
        </div>
      </form>
    </AppModal>

    <!-- Sign Out Confirmation -->
    <!-- For a guest this is destructive, not routine: the anonymous uid is the
         only credential and it lives in local storage, so signing out makes the
         account permanently unreachable and takes the data with it. Say so, and
         offer the linking flow, which keeps the uid and every row. -->
    <ConfirmDialog
      v-model="showSignOutConfirm"
      :title="isAnonymous ? t('confirm.sign_out_guest_title') : t('confirm.sign_out_title')"
      :message="isAnonymous ? t('confirm.sign_out_guest_message') : t('confirm.sign_out_message')"
      :confirm-text="isAnonymous ? t('confirm.sign_out_guest_confirm') : t('confirm.sign_out_title')"
      :cancel-text="t('common.cancel')"
      :alt-text="isAnonymous ? t('confirm.sign_out_guest_keep') : undefined"
      :variant="isAnonymous ? 'danger' : 'warning'"
      @confirm="executeSignOut"
      @alt="handleUpgradeInstead"
    />

    <!-- Reset Password Confirmation -->
    <ConfirmDialog
      v-model="showResetPasswordConfirm"
      :title="t('confirm.reset_password_title')"
      :message="resetPasswordMessage"
      :confirm-text="t('confirm.reset_password_title')"
      :cancel-text="t('common.cancel')"
      variant="info"
      @confirm="executeResetPassword"
    />

    <!-- Delete Account Confirmation -->
    <ConfirmDialog
      v-model="showDeleteAccountConfirm"
      :title="t('settings.delete_account_title')"
      :message="t('settings.delete_account_confirm')"
      :confirm-text="t('settings.delete_account_button')"
      :cancel-text="t('common.cancel')"
      variant="danger"
      @confirm="executeDeleteAccount"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useToast } from "../composables/useToast"
import { deleteAccount } from "../services/accountService"
import { authService } from "../services/authService"
import { currentUser, isAnonymous, logout, resetPassword } from "../stores/authStore"
import { firestoreState, updateUserPreferences } from "../stores/firestoreStore"
import { householdStore } from "../stores/householdStore"
import { requestAccountUpgrade } from "../stores/uiStore"
import { type Household, HouseholdRole } from "../types/household"
import { logger } from "../utils/logger"
import ConfirmDialog from "./ConfirmDialog.vue"
import { ActionRow, AppAlert, AppAvatar, AppBadge, AppButton, AppInput, AppModal, ImagePicker } from "./ui"

const { t, locale } = useI18n()
const toast = useToast()

interface Emits {
  (e: "close"): void
  (e: "upgrade"): void
  (e: "navigate", tab: string): void
}

const emit = defineEmits<Emits>()

const user = currentUser
const households = computed(() => householdStore.state.households)
const pendingInvitations = computed(() => householdStore.state.pendingInvitations)

// The uploaded avatar. Held locally so the ImagePicker can round-trip
// v-model, and written straight back to preferences on every change — there's
// no Save button on a photo, picking it is the confirmation.
const avatarImageId = ref<string | null>(firestoreState.preferences.value?.avatarImageId ?? null)

watch(
  () => firestoreState.preferences.value?.avatarImageId,
  (imageId) => {
    avatarImageId.value = imageId ?? null
  },
)

async function saveAvatar(imageId: string | null) {
  try {
    await updateUserPreferences({ avatarImageId: imageId })
  } catch (error) {
    logger.warn("Failed to save avatar", { error })
    toast.error(t("toasts.save_failed"))
  }
}

const showEditProfile = ref(false)
const showSignOutConfirm = ref(false)
const showResetPasswordConfirm = ref(false)
const showDeleteAccountConfirm = ref(false)
const isDeletingAccount = ref(false)
const isSaving = ref(false)
const profileError = ref("")
const profileSuccess = ref("")

const resetPasswordMessage = computed(() =>
  user.value?.email ? t("confirm.reset_password_message", { email: user.value.email }) : "",
)

const editForm = ref({
  displayName: user.value?.displayName || "",
  photoURL: user.value?.photoURL || "",
})

const accountType = computed(() => {
  if (!user.value) return t("profile.type_unknown")
  if (user.value.isAnonymous) return t("profile.type_guest")
  if (user.value.providerId === "google.com") return "Google"
  if (user.value.providerId === "apple.com") return "Apple"
  return t("profile.type_email")
})

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return t("profile.type_unknown")
  const date = new Date(dateString)
  return date.toLocaleDateString(locale.value === "no" ? "nb-NO" : "en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  })
}

function getUserRole(household: Household): string {
  if (!user.value) return ""
  const member = household.members.find((m) => m.userId === user.value?.uid)
  if (!member) return ""
  return member.role === HouseholdRole.ADMINISTRATOR
    ? t("household.role_administrator")
    : t("household.role_family_member")
}

function openHouseholdManager() {
  emit("navigate", "household")
  emit("close")
}

function closeEditProfile() {
  showEditProfile.value = false
  profileError.value = ""
  profileSuccess.value = ""
  // Reset form to current values
  editForm.value = {
    displayName: user.value?.displayName || "",
    photoURL: user.value?.photoURL || "",
  }
}

async function handleSaveProfile() {
  if (!currentUser.value) return

  isSaving.value = true
  profileError.value = ""
  profileSuccess.value = ""

  try {
    // Persist both fields the form collects — the photo URL used to be
    // previewed and then silently discarded.
    await authService.updateProfile({
      displayName: editForm.value.displayName.trim(),
      photoURL: editForm.value.photoURL.trim() || null,
    })
    profileSuccess.value = t("profile.profile_updated")

    setTimeout(() => {
      closeEditProfile()
    }, 1500)
  } catch (error: any) {
    profileError.value = t("profile.profile_update_failed")
  } finally {
    isSaving.value = false
  }
}

function handleResetPassword() {
  if (!user.value?.email) {
    toast.error(t("toasts.password_reset_failed"))
    return
  }
  showResetPasswordConfirm.value = true
}

async function executeResetPassword() {
  if (!user.value?.email) return
  try {
    await resetPassword(user.value.email)
    toast.success(t("toasts.password_reset_sent"))
  } catch (error) {
    logger.error("Failed to send password reset email", error)
    toast.error(t("toasts.password_reset_failed"))
  }
}

function handleSignOut() {
  showSignOutConfirm.value = true
}

// The guest chose to keep their data instead. requestAccountUpgrade opens the
// *linking* modal (not register/login), which preserves the anonymous uid — so
// every row they created as a guest stays theirs. Close the profile sheet or
// the upgrade modal opens behind it.
function handleUpgradeInstead() {
  emit("close")
  requestAccountUpgrade()
}

async function executeSignOut() {
  try {
    await logout()
    toast.success(t("toasts.signed_out"))
    emit("close")
  } catch (error) {
    logger.error("Failed to sign out", error)
    toast.error(t("toasts.sign_out_failed"))
  }
}

// Mirrors SettingsView.onDeleteAccount: the server delete cascades every row,
// then a local sign-out clears caches + outbox. The sign-out may reject — the
// token died with the account — so it is best-effort; the reload lands on the
// welcome screen either way.
async function executeDeleteAccount() {
  isDeletingAccount.value = true
  try {
    await deleteAccount()
    try {
      await logout()
    } catch {
      // token already invalidated by the deletion
    }
    window.location.reload()
  } catch (error) {
    logger.error("Account deletion failed", error)
    toast.error(t("settings.delete_account_failed"))
    isDeletingAccount.value = false
  }
}

function handleUpgradeAccount() {
  emit("upgrade")
  emit("close")
}
</script>
